import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, HardDrive, Wifi, Server, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Label,
} from 'recharts';
import { LineChart as LineChartIcon } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { ReplicationView } from './ReplicationView';

interface MetricsTabProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

const ScrollableLegend = (props: any) => {
  const { payload, hoveredSeries, onHoverSeries } = props;
  if (!payload || payload.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-14 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
      {payload.map((entry: any, index: number) => {
        const seriesKey = entry.dataKey || entry.value;
        const isHovered = hoveredSeries === seriesKey;
        const isDimmed = hoveredSeries !== null && !isHovered;
        return (
          <div
            key={`legend-item-${index}`}
            onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
            onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
            className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
              }`}
          >
            <span
              className="w-2 h-2 rounded-full inline-block shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-200 text-[9px] truncate max-w-[130px]" title={entry.value}>
              {entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const MetricsTab: React.FC<MetricsTabProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [hostGraphData, setHostGraphData] = useState<any>(null);

  const isES = ['opensearch', 'elasticsearch'].includes((dbType || '').toLowerCase());

  // Legend Hover States
  const [hoveredCpu, setHoveredCpu] = useState<string | null>(null);
  const [hoveredMem, setHoveredMem] = useState<string | null>(null);
  const [hoveredNet, setHoveredNet] = useState<string | null>(null);
  const [hoveredDisk, setHoveredDisk] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60');

    const params: any = {
      cluster_id: activeCluster,
      minutes,
    };
    if (clientId) params.client_id = clientId;

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/host', { params });
      setHostGraphData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MetricsTab fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels = hostGraphData?.labels || [];
  const series = hostGraphData?.series || {};

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60');
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = formatTimeLabels(labels);
  const formattedData = labels.map((_: string, idx: number) => {
    const rawRx = series.host_net_rx_bytes?.[idx] || 0;
    const rawTx = series.host_net_tx_bytes?.[idx] || 0;
    const rawDiskUsed = series.disk_used_mb?.[idx] || 0;
    const rawDiskTotal = series.disk_total_mb?.[idx] || 0;

    const netRxMB = rawRx > 1000 ? Number((rawRx / (1024 * 1024)).toFixed(2)) : Number(rawRx.toFixed(2));
    const netTxMB = rawTx > 1000 ? Number((rawTx / (1024 * 1024)).toFixed(2)) : Number(rawTx.toFixed(2));

    const diskUsedGB = Number((rawDiskUsed / 1024).toFixed(2));
    const diskTotalGB = Number((rawDiskTotal / 1024).toFixed(2));

    const rawMemTotal = series.host_mem_total_mb?.[idx] || 0;
    const rawSwapUsed = series.host_swap_used_mb?.[idx] || 0;

    return {
      time: timeLabels[idx],
      user_cpu: series.host_cpu_user_pct?.[idx] || 0,
      sys_cpu: series.host_cpu_sys_pct?.[idx] || 0,
      iowait_cpu: series.host_cpu_iowait_pct?.[idx] || 0,
      steal_cpu: series.host_cpu_steal_pct?.[idx] || 0,
      mem_used: series.host_mem_used_pct?.[idx] || 0,
      mem_total_mb: typeof rawMemTotal === 'number' ? Number(rawMemTotal.toFixed(1)) : rawMemTotal,
      swap_used_mb: typeof rawSwapUsed === 'number' ? Number(rawSwapUsed.toFixed(1)) : rawSwapUsed,
      net_rx: netRxMB,
      net_tx: netTxMB,
      disk_used: diskUsedGB,
      disk_total: diskTotalGB,
    };
  });

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) => Object.keys(item).some((k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0));
  };

  const getOpacity = (hovered: string | null, key: string) => {
    if (hovered === null) return 1;
    return hovered === key ? 1 : 0.1;
  };

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3.5 relative">
      {/* Section 1: System & Operating Host Metrics */}
      <div className="space-y-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <div className="p-1.5 rounded bg-orange-500 text-white">
              <LineChartIcon className="w-4 h-4" />
            </div>
            Server Performance
          </h2>
        </div>

        {/* Host Row 1: CPU Utilization & Memory */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
              <Cpu className="w-4 h-4 text-blue-400" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">CPU Utilization %</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorUserCpu" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} ticks={[0, 50, 100]} />
                    <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredCpu} onHoverSeries={setHoveredCpu} />} />
                    <Area
                      type="linear"
                      dataKey="user_cpu"
                      name="User CPU %"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredCpu, 'user_cpu')}
                      fillOpacity={getOpacity(hoveredCpu, 'user_cpu') * 0.4}
                      fill="url(#colorUserCpu)"
                      strokeWidth={hoveredCpu === 'user_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="sys_cpu"
                      name="System CPU %"
                      stroke="#ef4444"
                      strokeOpacity={getOpacity(hoveredCpu, 'sys_cpu')}
                      strokeWidth={hoveredCpu === 'sys_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="iowait_cpu"
                      name="I/O Wait CPU %"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredCpu, 'iowait_cpu')}
                      strokeWidth={hoveredCpu === 'iowait_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="linear"
                      dataKey="steal_cpu"
                      name="Steal CPU %"
                      stroke="#a855f7"
                      strokeOpacity={getOpacity(hoveredCpu, 'steal_cpu')}
                      strokeWidth={hoveredCpu === 'steal_cpu' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No CPU metric telemetry available</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
              <Server className="w-4 h-4 text-green-400" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Used Memory & Swap</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorMem" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis yAxisId="pct" stroke="#9ca3af" fontSize={10} tickLine={false} domain={[0, 100]} ticks={[0, 50, 100]} tickFormatter={(v) => `${v}%`} />
                    <YAxis yAxisId="mb" orientation="right" stroke="#9ca3af" fontSize={10} tickLine={false} tickFormatter={(v) => `${v}`}>
                      <Label
                        value="GB"
                        angle={90}
                        position="insideRight"
                        offset={30}
                        fill="#9ca3af"
                        fontSize={11}
                      />
                    </YAxis>
                    <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredMem} onHoverSeries={setHoveredMem} />} />
                    <Area
                      yAxisId="pct"
                      type="monotone"
                      dataKey="mem_used"
                      name="Memory Used %"
                      stroke="#10b981"
                      strokeOpacity={getOpacity(hoveredMem, 'mem_used')}
                      fillOpacity={getOpacity(hoveredMem, 'mem_used') * 0.4}
                      fill="url(#colorMem)"
                      strokeWidth={hoveredMem === 'mem_used' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      yAxisId="mb"
                      type="monotone"
                      dataKey="mem_total_mb"
                      name="Total Memory (GB)"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredMem, 'mem_total_mb')}
                      strokeWidth={hoveredMem === 'mem_total_mb' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      yAxisId="mb"
                      type="monotone"
                      dataKey="swap_used_mb"
                      name="Swap Used (GB)"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredMem, 'swap_used_mb')}
                      strokeWidth={hoveredMem === 'swap_used_mb' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No memory metric telemetry available</span>
              )}
            </div>
          </div>
        </div>

        {/* Host Row 3: Network & Disk */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/40 border border-gray-700/60 rounded-xl p-4 shadow-xl">
          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
              <Wifi className="w-4 h-4 text-purple-400" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Network Throughput (MB/s)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formattedData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} tickFormatter={(v) => `${v}`}> 
                      <Label
                        value="MB/s"
                        angle={-90}
                        position="insideLeft"
                        offset={20}
                        fill="#9ca3af"
                        fontSize={11}
                      /> </YAxis>
                    <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredNet} onHoverSeries={setHoveredNet} />} />
                    <Line
                      type="monotone"
                      dataKey="net_rx"
                      name="Received MB/s"
                      stroke="#3b82f6"
                      strokeOpacity={getOpacity(hoveredNet, 'net_rx')}
                      strokeWidth={hoveredNet === 'net_rx' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="net_tx"
                      name="Sent MB/s"
                      stroke="#a855f7"
                      strokeOpacity={getOpacity(hoveredNet, 'net_tx')}
                      strokeWidth={hoveredNet === 'net_tx' ? 2.5 : 1.5}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No network metric telemetry available</span>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3 border-b border-gray-700/50 pb-2">
              <HardDrive className="w-4 h-4 text-amber-400" />
              <h4 className="text-xs font-bold text-gray-200 uppercase tracking-wider">Disk Usage (GB)</h4>
            </div>
            <div className="h-40 w-full flex items-center justify-center">
              {hasData(formattedData) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={formattedData}>
                    <defs>
                      <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                    <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={3} tickFormatter={(v) => `${v}`}>
                      <Label
                        value="GB"
                        angle={-90}
                        position="insideLeft"
                        offset={20}
                        fill="#9ca3af"
                        fontSize={11}
                      /> 
                    </YAxis>
                    <Tooltip wrapperStyle={{ zIndex: 1000 }} contentStyle={{ backgroundColor: '#111827', borderColor: '#374151', fontSize: '11px' }} />
                    <Legend content={<ScrollableLegend hoveredSeries={hoveredDisk} onHoverSeries={setHoveredDisk} />} />
                    <Area
                      type="monotone"
                      dataKey="disk_used"
                      name="Disk Used (GB)"
                      stroke="#f59e0b"
                      strokeOpacity={getOpacity(hoveredDisk, 'disk_used')}
                      fillOpacity={getOpacity(hoveredDisk, 'disk_used') * 0.4}
                      fill="url(#colorDisk)"
                      strokeWidth={hoveredDisk === 'disk_used' ? 2.5 : 1.5}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="disk_total"
                      name="Disk Total (GB)"
                      stroke="#6b7280"
                      strokeOpacity={getOpacity(hoveredDisk, 'disk_total')}
                      strokeWidth={1}
                      strokeDasharray="4 4"
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <span className="text-xs text-gray-500 italic">No disk usage telemetry available</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isES && (
        <div className="border-t border-gray-800 pt-4">
          <ReplicationView
            activeCluster={activeCluster}
            timeRange={timeRange}
            clientId={clientId}
            refreshKey={refreshKey}
          />
        </div>
      )}
    </div>
  );
};

export default MetricsTab;
